import { type ReactNode, useState } from 'react';
import { Modal } from '@/components/ui/modal';
import {
  PHRASE_MAX as MAX,
  PHRASE_HINT as HINT,
  hasForbidden,
} from '@/utils/phrase-validation';
import { useEditPhrase } from '../hooks/use-edit-phrase';

type Props = { open: boolean; onClose: () => void; current: string };

const TA_CLS =
  'w-full resize-none rounded-lg border border-white/[0.06] bg-surface px-md py-sm text-sm text-text-primary placeholder:text-text-tertiary focus:border-brand-red focus:outline-none focus:ring-2 focus:ring-brand-red/30';

function HintRow({ value }: { value: string }): ReactNode {
  const invalid = hasForbidden(value);
  const over = value.length > MAX;
  const hintCls = invalid ? 'text-error' : 'text-text-tertiary';
  const cntCls = over ? 'text-error' : 'text-text-tertiary';
  return (
    <div className="mt-xs flex items-start justify-between gap-sm">
      <span className={`text-[11px] leading-snug ${hintCls}`}>{HINT}</span>
      <span className={`text-xs tabular-nums ${cntCls}`}>
        {value.length}/{MAX}
      </span>
    </div>
  );
}

function PhraseInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}): ReactNode {
  return (
    <div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={MAX}
        rows={3}
        className={TA_CLS}
        placeholder="Escribe tu frase personal..."
      />
      <HintRow value={value} />
    </div>
  );
}

type ActionProps = {
  onCancel: () => void;
  onSave: () => void;
  disabled: boolean;
};

function Actions({ onCancel, onSave, disabled }: ActionProps): ReactNode {
  return (
    <div className="flex justify-end gap-sm">
      <button
        onClick={onCancel}
        className="rounded-lg px-md py-sm text-xs text-text-secondary hover:text-text-primary"
      >
        Cancelar
      </button>
      <button
        onClick={onSave}
        disabled={disabled}
        className="rounded-lg bg-brand-red px-md py-sm text-xs font-medium text-white hover:bg-brand-coral disabled:opacity-50"
      >
        Guardar
      </button>
    </div>
  );
}

export function EditPhraseModal({ open, onClose, current }: Props): ReactNode {
  const [value, setValue] = useState(current);
  const { mutate, isPending } = useEditPhrase();
  const handleSave = (): void => {
    mutate(value);
    onClose();
  };
  return (
    <Modal open={open} onClose={onClose} title="Editar frase" size="sm">
      <div className="mt-md space-y-md">
        <PhraseInput value={value} onChange={setValue} />
        <Actions
          onCancel={onClose}
          onSave={handleSave}
          disabled={isPending || value.length > MAX || hasForbidden(value)}
        />
      </div>
    </Modal>
  );
}
